/**
 * An order's money, and the vocabulary its lines are written in — with NO
 * runtime dependencies.
 *
 * Its own module, reachable as `@fitting-room/contracts/order-math`, for the
 * reason `./appointment-status.ts` is: the console previews a line's discount
 * and the order's tax as the stylist types, and the API stores what it works
 * out on save. Both have to reach the SAME cent, so both import this one file
 * rather than one of them keeping a copy that drifts by a rounding rule.
 * `docs/22-back-office.md` § "Orders".
 *
 * Integer minor units throughout, and no division that is not rounded on
 * purpose: a float anywhere in here is a cent somebody argues about at the
 * counter.
 */

export const ORDER_STATUSES = ['open', 'completed', 'canceled'] as const
export type OrderStatusValue = (typeof ORDER_STATUSES)[number]

/**
 * The customer's five measurements, in the order the invoice prints them:
 * bust, waist, hip, waist to hem (`wh`) and hollow to hem (`hh`). Here, not
 * beside `orderMeasurementsSchema`, so the console draws its fields from the
 * same list without importing zod.
 */
export const ORDER_MEASUREMENT_KEYS = ['bust', 'waist', 'hip', 'wh', 'hh'] as const
export type OrderMeasurementKey = (typeof ORDER_MEASUREMENT_KEYS)[number]

/**
 * The five things a line can be. Two ways to buy a gown — cut to her
 * measurements, or the physical piece off the rack — and the three things
 * that ride along with one.
 */
export const ORDER_LINE_KINDS = ['made_to_order', 'off_the_rack', 'accessory', 'alteration', 'other'] as const
export type OrderLineKindValue = (typeof ORDER_LINE_KINDS)[number]

/**
 * What a line IS, beside how it is bought — the group the order page and the
 * invoice draw it in: Dresses, Accessories, and Other accessories (the
 * bridal heels).
 *
 * A second field rather than more kinds, because the two questions are
 * independent: a dress and an accessory are each either a special order
 * (`made_to_order`, cut or ordered in for this customer) or off the rack, and
 * `kind` already carries that — the fulfillment path, the rack hold and the
 * commission default all read it and keep working unchanged. Folding the
 * group into `kind` would have doubled the kinds and every switch over them.
 *
 * `other` is the group of the two kinds that are neither a dress nor
 * something worn with one — `alteration` and `other`, written before the
 * custom cost on a line replaced the alteration line.
 */
export const LINE_MERCHANDISE = ['dress', 'accessory', 'shoes', 'other'] as const
export type LineMerchandiseValue = (typeof LINE_MERCHANDISE)[number]

/**
 * The order list's "where it stands" filters (`?show=`, every one must hold)
 * and the line kinds it filters on (`?kind=`, any one will do). Here rather
 * than beside the list's schema so the console draws its chips from the same
 * list without importing zod. What each flag means is `orderListFlags` in
 * `domain`; `docs/22` § "Orders".
 */
export const ORDER_LIST_FLAGS = ['pending', 'balance_due', 'not_ordered', 'not_arrived', 'ready_for_pickup', 'pickup_soon', 'wedding_soon'] as const
export const ORDER_LIST_KINDS = ['made_to_order', 'off_the_rack'] as const

/**
 * The group a line of this kind belongs to when nobody said: every line
 * written before the groups existed reads as it was written.
 */
export function defaultMerchandise(kind: OrderLineKindValue): LineMerchandiseValue {
  switch (kind) {
    case 'made_to_order':
    case 'off_the_rack':
      return 'dress'
    case 'accessory':
      return 'accessory'
    case 'alteration':
    case 'other':
      return 'other'
  }
}

/** The kinds a line in each group may be bought as. */
export const MERCHANDISE_KINDS: Readonly<Record<LineMerchandiseValue, readonly OrderLineKindValue[]>> = {
  dress: ['made_to_order', 'off_the_rack'],
  // `accessory` is the kind accessories were written as before the groups.
  accessory: ['made_to_order', 'off_the_rack', 'accessory'],
  shoes: ['made_to_order', 'off_the_rack'],
  other: ['alteration', 'other'],
}

/**
 * `percent` stores its value in basis points (1000 is 10%), `amount` in minor
 * units — integers both, so a 12.5% discount is 1250 and never 12.5.
 */
export const DISCOUNT_KINDS = ['percent', 'amount'] as const
export type DiscountKindValue = (typeof DISCOUNT_KINDS)[number]

/**
 * Where a line is on its way to the customer. `docs/22-back-office.md`
 * § "Pickup and arrival" has the two paths; `./fulfillment-path.ts` says
 * which way a line may move.
 */
export const LINE_FULFILLMENTS = ['none', 'to_order', 'ordered', 'shipped', 'arrived', 'on_hold', 'picked_up'] as const
export type LineFulfillmentValue = (typeof LINE_FULFILLMENTS)[number]

/**
 * Who earns commission on a line unless somebody says otherwise: the gown and
 * what is sold with it, yes; the seamstress's work and anything else, no.
 * The owner has not confirmed the base yet (`docs/22` § "Pending"), which is
 * why it is a default the stylist can flip per line rather than a rule.
 */
export function defaultCommissionable(kind: OrderLineKindValue): boolean {
  // Shoes are written as `off_the_rack` or `made_to_order` in the `shoes`
  // group, so they earn commission by default with the gown they go with.
  return kind === 'made_to_order' || kind === 'off_the_rack' || kind === 'accessory'
}

/**
 * `numerator / denominator`, rounded half up, for non-negative integers.
 *
 * Integer arithmetic rather than `Math.round(a / b)`: the float division is
 * exact often enough to pass every test someone thinks to write and wrong on
 * the order that lands on .5 after representation error.
 */
export function roundHalfUp(numerator: number, denominator: number): number {
  return Math.floor((2 * numerator + denominator) / (2 * denominator))
}

/** quantity × unit price. What a discount is a share of, and what the cap is measured against. */
export function lineGrossMinor(line: { readonly quantity: number; readonly unitPriceMinor: number }): number {
  return line.quantity * line.unitPriceMinor
}

/**
 * What a line comes to: quantity × unit price, plus its custom cost, less
 * its discount — the invoice's "Total dress price", and what commission is
 * worked out on.
 *
 * The custom cost (the modifications cut into a special order, the
 * invoice's "Customization cost") is added AFTER the discount and is never
 * part of what the discount is a share of. A percentage off is the boutique
 * giving on the gown; the custom cost is the workroom's charge for this
 * customer's changes, priced to cover itself. Measuring the cap against the
 * price alone also keeps the cap meaning what the owner set it to: adding a
 * modification must not widen how much may come off the dress.
 */
export function lineNetMinor(line: {
  readonly quantity: number
  readonly unitPriceMinor: number
  readonly customCostMinor?: number | undefined
  readonly discountMinor: number
}): number {
  const gross = lineGrossMinor(line)
  return gross + (line.customCostMinor ?? 0) - Math.min(line.discountMinor, gross)
}

export interface LineDiscount {
  readonly kind: DiscountKindValue
  /** Basis points for `percent`, minor units for `amount`. */
  readonly value: number
}

/**
 * What a discount takes off a line's gross, in minor units. A percentage is
 * rounded half up to the cent; an amount is what was typed. Never more than
 * the gross — a line cannot go negative, whatever was entered.
 */
export function lineDiscountMinor(grossMinor: number, discount: LineDiscount | null | undefined): number {
  if (!discount || discount.value <= 0) return 0
  const off = discount.kind === 'percent' ? roundHalfUp(grossMinor * discount.value, 10_000) : discount.value
  return Math.min(off, grossMinor)
}

/** The cap as basis points: 20 → 2000, 12.5 → 1250. */
export function capBps(maxDiscountPct: number): number {
  return Math.round(maxDiscountPct * 100)
}

/** The largest amount a line of this gross may come off, rounded down — the cap is a ceiling. */
export function discountCapMinor(grossMinor: number, maxDiscountPct: number): number {
  return Math.floor((grossMinor * capBps(maxDiscountPct)) / 10_000)
}

/**
 * Is this discount over the boutique's cap on this line?
 *
 * Compared in the discount's own terms, so a percentage at exactly the cap is
 * allowed even when its rounding lands a cent above the floor of the cap in
 * dollars: 20% of $10.05 is $2.01 and that is still 20%.
 */
export function discountOverCap(discount: LineDiscount | null | undefined, grossMinor: number, maxDiscountPct: number): boolean {
  if (!discount || discount.value <= 0) return false
  if (discount.kind === 'percent') return discount.value > capBps(maxDiscountPct)
  return discount.value * 10_000 > grossMinor * capBps(maxDiscountPct)
}

export interface TotalsLine {
  readonly quantity: number
  readonly unitPriceMinor: number
  /** The line's custom cost; absent is none. See `lineNetMinor`. */
  readonly customCostMinor?: number | undefined
  /** As stored on the line when it was written: see `lineDiscountMinor`. */
  readonly discountMinor: number
  readonly taxable: boolean
}

export interface OrderTotalsValue {
  /** Σ quantity × unit. */
  grossMinor: number
  /** Σ custom costs. */
  customCostMinor: number
  /** Σ discounts. */
  discountMinor: number
  /** Σ net — gross plus custom cost less discount — of every line. */
  subtotalMinor: number
  /** Σ net of the taxable lines: what the tax is charged on. */
  taxableMinor: number
  taxMinor: number
  /** subtotal + tax. */
  totalMinor: number
}

/**
 * An order's totals, at the rate it was written at.
 *
 * Tax is worked out once on the ORDER, not per line and summed: rounding each
 * line and adding the cents gives a different answer from the register's,
 * and the register is what the customer compares against.
 */
export function orderTotals(lines: readonly TotalsLine[], taxRateBps: number): OrderTotalsValue {
  let grossMinor = 0
  let customCostMinor = 0
  let discountMinor = 0
  let taxableMinor = 0
  for (const line of lines) {
    const gross = lineGrossMinor(line)
    const custom = line.customCostMinor ?? 0
    const discount = Math.min(line.discountMinor, gross)
    grossMinor += gross
    customCostMinor += custom
    discountMinor += discount
    if (line.taxable) taxableMinor += gross + custom - discount
  }
  const subtotalMinor = grossMinor + customCostMinor - discountMinor
  const taxMinor = roundHalfUp(taxableMinor * taxRateBps, 10_000)
  return { grossMinor, customCostMinor, discountMinor, subtotalMinor, taxableMinor, taxMinor, totalMinor: subtotalMinor + taxMinor }
}

/* ---------------------------------- dates ---------------------------------- */

/**
 * `YYYY-MM-DD` moved by whole days, on the calendar alone — no timezone, no
 * clock: noon UTC is a moment every calendar day has exactly once.
 */
export function shiftDay(day: string, days: number): string {
  const at = new Date(`${day}T12:00:00.000Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}

/**
 * How long before the wedding the gown has to be in the customer's hands:
 * the owner's rule of thumb, which leaves time for the fittings. The order's
 * required date starts here and the stylist moves it.
 */
export const REQUIRED_DAYS_BEFORE_WEDDING = 45

/** Wedding date − 45 days, or nothing when there is no wedding date to count back from. */
export function defaultRequiredDate(weddingDate: string | undefined): string | undefined {
  return weddingDate ? shiftDay(weddingDate, -REQUIRED_DAYS_BEFORE_WEDDING) : undefined
}

/**
 * When the customer is expected to pick up if nobody knows better: twenty
 * weeks after the order, which is how long a special order usually takes to
 * arrive.
 */
export const EXPECTED_PICKUP_WEEKS = 20

export type ExpectedPickupSourceValue = 'set' | 'eta' | 'held' | 'default'

export interface ExpectedPickupValue {
  /** `YYYY-MM-DD`. */
  date: string
  /**
   * Typed by staff; the latest special-order ETA; the day a piece is held
   * until; or the order date + 20 weeks.
   */
  source: ExpectedPickupSourceValue
}

/**
 * The day the customer is expected to pick up — planned on every order,
 * paid in full or not: an order with nothing owed still has a gown to hand
 * over.
 *
 * Worked out on every read rather than stored, with what staff typed
 * winning: the ETA that decides it usually arrives weeks after the order is
 * written, from the supplier, and a date stored on the day of the order
 * would still say "+ 20 weeks" long after the supplier said otherwise.
 * `set` is the order's own `expectedPickupDate`. Otherwise the latest of
 * what is known about when every piece will be in the boutique for the
 * customer — the ETA that counts (`manualEta ?? systemEta`) on each
 * special-order line, and the day each held piece is held until
 * (`heldUntil`, which only a piece off the rack or from stock carries) —
 * `eta` or `held` after whichever is latest, the ETA on a tie. Neither
 * known, `orderDay` + 20 weeks.
 *
 * Only the lines still to go home count, while there are any: a gown picked
 * up last month says nothing about when the veil still on order will be
 * collected. Once every line that can go has gone, they all count again, so
 * a finished order still reads the day its dates said.
 */
export function expectedPickup(input: {
  readonly set?: string | undefined
  /** The boutique day the order was written. */
  readonly orderDay: string
  readonly lines: readonly {
    readonly kind: OrderLineKindValue
    readonly manualEta?: string | undefined
    readonly systemEta?: string | undefined
    readonly heldUntil?: string | undefined
    /** Absent reads as still to go home. */
    readonly fulfillment?: LineFulfillmentValue | undefined
  }[]
}): ExpectedPickupValue {
  if (input.set) return { date: input.set, source: 'set' }
  const waiting = input.lines.filter((line) => line.fulfillment !== 'picked_up' && line.fulfillment !== 'none')
  let latest: ExpectedPickupValue | undefined
  for (const line of waiting.length > 0 ? waiting : input.lines) {
    const eta = line.kind === 'made_to_order' ? (line.manualEta ?? line.systemEta) : undefined
    if (eta && (!latest || eta > latest.date || (eta === latest.date && latest.source === 'held'))) latest = { date: eta, source: 'eta' }
    if (line.heldUntil && (!latest || line.heldUntil > latest.date)) latest = { date: line.heldUntil, source: 'held' }
  }
  return latest ?? { date: shiftDay(input.orderDay, EXPECTED_PICKUP_WEEKS * 7), source: 'default' }
}

export type BalanceDueSourceValue = 'set' | 'pickup'

export interface BalanceDueValue {
  /** `YYYY-MM-DD`. */
  date: string
  /** Typed by staff, or the expected pickup day. */
  source: BalanceDueSourceValue
}

/**
 * The day the balance is due, while there is one: the day the customer
 * picks up, which is when the boutique asks for the rest — so the expected
 * pickup (`expectedPickup`), moving when it moves, unless staff set the
 * order's own `balanceDueDate`, which wins.
 *
 * `null` when nothing is owed (`balanceMinor` ≤ 0), whatever was set: a
 * paid order has no balance to fall due, and a date beside it would say it
 * had.
 */
export function balanceDue(input: {
  readonly set?: string | undefined
  /** total − paid, as `orderMoney` has it. */
  readonly balanceMinor: number
  readonly pickup: ExpectedPickupValue
}): BalanceDueValue | null {
  if (input.balanceMinor <= 0) return null
  if (input.set) return { date: input.set, source: 'set' }
  return { date: input.pickup.date, source: 'pickup' }
}

/* --------------------------------- pickup --------------------------------- */

/**
 * The pickup appointment an order's pickup day is read from, or `undefined`.
 * Pass the order's pickup appointments only — which types are pickups is the
 * tenant's to say.
 *
 * Of the ones booked or kept — a canceled one or a no-show says nothing
 * about when the customer comes — the next from `now`, or the latest when
 * they are all past. A pickup in two parts, the gown now and the veil still
 * on order, reads the part still to come; once both are behind, the last.
 */
export function pickupAppointment<T extends { readonly scheduledAt: string; readonly status: string }>(
  appointments: readonly T[],
  now: string,
): T | undefined {
  const at = Date.parse(now)
  const live = appointments
    .filter((appointment) => appointment.status === 'booked' || appointment.status === 'completed')
    .sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt))
  return live.find((appointment) => Date.parse(appointment.scheduledAt) >= at) ?? live[live.length - 1]
}

/* -------------------------------- payments -------------------------------- */

/**
 * A Shopify order number as the shop says it — `1147`, the number on the
 * receipt — from however it was typed: `#1147`, ` 1147 `. Empty is none.
 */
export function normalizeShopifyOrderNo(raw: string): string | undefined {
  const trimmed = raw.trim().replace(/^#+\s*/, '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * What a payment on an order is. A deposit and a payment both count toward
 * what has been paid — the two words are the counter's, so the ledger reads
 * the way the receipt did — and a refund comes off it. Rental's kinds
 * (security deposits, fees) are tperent's and not here. `docs/22` § "Payments".
 */
export const PAYMENT_KINDS = ['deposit', 'payment', 'refund'] as const
export type PaymentKindValue = (typeof PAYMENT_KINDS)[number]

/** How the money moved. `shopify` is a charge run through Shopify — POS or checkout. */
export const PAYMENT_METHODS = ['shopify', 'card', 'cash', 'zelle', 'check', 'other'] as const
export type PaymentMethodValue = (typeof PAYMENT_METHODS)[number]

/** ClickUp's "In Full / Partial", with the two ends named. Derived, never stored. */
export const PAYMENT_STATES = ['unpaid', 'partial', 'paid', 'overpaid'] as const
export type PaymentStateValue = (typeof PAYMENT_STATES)[number]

export interface LedgerEntry {
  readonly kind: PaymentKindValue
  readonly amountMinor: number
  /** Present once voided. A voided entry stays on the ledger and counts for nothing. */
  readonly voidedAt?: string | undefined
}

/** A refund takes money back out; everything else brings it in. */
export function signedPaymentMinor(entry: Pick<LedgerEntry, 'kind' | 'amountMinor'>): number {
  return entry.kind === 'refund' ? -entry.amountMinor : entry.amountMinor
}

/** Σ deposits and payments − Σ refunds, over the entries nobody voided. */
export function paidMinor(entries: readonly LedgerEntry[]): number {
  let paid = 0
  for (const entry of entries) if (!entry.voidedAt) paid += signedPaymentMinor(entry)
  return paid
}

/**
 * Where an order stands against what it comes to.
 *
 * `paid` is exactly the total, to the cent — including an order that comes to
 * nothing and has had nothing paid on it, which owes nothing. Over the total
 * is `overpaid` whatever made it so: a line taken off after the deposit, or a
 * discount given late. Neither is refused; the badge says so and a refund
 * settles it.
 */
export function paymentState(totalMinor: number, paid: number): PaymentStateValue {
  if (paid > totalMinor) return 'overpaid'
  if (paid === totalMinor) return 'paid'
  return paid > 0 ? 'partial' : 'unpaid'
}

export interface OrderMoneyValue {
  totalMinor: number
  paidMinor: number
  /** total − paid. Negative when overpaid. */
  balanceMinor: number
  paymentState: PaymentStateValue
}

/** An order's total against its ledger. */
export function orderMoney(totalMinor: number, entries: readonly LedgerEntry[]): OrderMoneyValue {
  const paid = paidMinor(entries)
  return { totalMinor, paidMinor: paid, balanceMinor: totalMinor - paid, paymentState: paymentState(totalMinor, paid) }
}

/* --------------------------------- invoice -------------------------------- */

/**
 * The most addresses one invoice is emailed to: the customer, a parent, a
 * planner — not a mailing list. Here, beside the arithmetic, rather than in
 * the schemas, so the console's dialog stops at the number the API refuses
 * past without importing every schema to learn it.
 */
export const MAX_INVOICE_RECIPIENTS = 10
